import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FadeIn, ChapterHeading } from './Reveal';
import { useLang } from '../i18n/LangContext';

const Phone = ({ tab, size, dimmed = false, onClick, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    aria-label={tab.label}
    className={`relative rounded-[2.4rem] bg-[#0B1424] p-[9px] border transition-[border-color,opacity,transform] duration-500 ${size} ${
      dimmed ? 'border-white/10 opacity-50 hover:opacity-80 scale-95' : 'border-white/20 shadow-[0_50px_100px_-24px_rgba(13,148,136,0.4)]'
    }`}
  >
    <div className="absolute top-[9px] left-1/2 -translate-x-1/2 w-[32%] h-[22px] bg-[#0B1424] rounded-b-xl z-10" aria-hidden="true" />
    <div className={`overflow-hidden rounded-[1.9rem] bg-black aspect-[1170/2532] ${tab.zoom || ''}`}>
      <img src={tab.img} alt={`KRUG aplikacija — ${tab.label}`} loading="lazy" className="w-full h-full object-cover object-top transition-transform duration-700" />
    </div>
  </button>
);

export const Showcase = () => {
  const { t } = useLang();
  const SHOWCASE_TABS = t.showcase.tabs;
  const [active, setActive] = useState(0);
  const tab = SHOWCASE_TABS[active];
  const prev = SHOWCASE_TABS[(active - 1 + SHOWCASE_TABS.length) % SHOWCASE_TABS.length];
  const next = SHOWCASE_TABS[(active + 1) % SHOWCASE_TABS.length];

  return (
    <section id="funkcije" data-testid="showcase-section" className="relative bg-krug-deep py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-krug-teal/40 to-transparent" aria-hidden="true" />
      <div className="absolute -bottom-32 left-1/2 -translate-x-1/2 w-[720px] h-[420px] rounded-full bg-krug-teal/10 blur-[140px]" aria-hidden="true" />

      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <ChapterHeading
          number="02"
          eyebrow={t.showcase.eyebrow}
          title={t.showcase.title}
          copy={t.showcase.copy}
          align="center"
        />

        <FadeIn delay={0.15} className="mt-12">
          <div data-testid="showcase-tabs" role="tablist" aria-label={t.showcase.tabsAria} className="flex flex-wrap justify-center gap-2.5">
            {SHOWCASE_TABS.map((t, i) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={i === active}
                data-testid={`showcase-tab-${t.id}`}
                onClick={() => setActive(i)}
                className={`rounded-full px-5 py-2.5 text-sm font-heading font-bold transition-colors duration-300 ${
                  i === active ? 'bg-krug-gold text-krug-navy' : 'border border-white/15 text-slate-300 hover:border-krug-tealbright/50 hover:text-white'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </FadeIn>

        <div className="mt-14 hidden md:flex items-end justify-center gap-8 lg:gap-14">
          <Phone tab={prev} size="w-[210px]" dimmed testid="showcase-phone-prev" onClick={() => setActive((active - 1 + SHOWCASE_TABS.length) % SHOWCASE_TABS.length)} />
          <AnimatePresence mode="wait">
            <motion.div
              key={tab.id}
              initial={{ opacity: 0, y: 26, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -18, scale: 0.98 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            >
              <Phone tab={tab} size="w-[290px]" testid="showcase-phone-active" onClick={() => {}} />
            </motion.div>
          </AnimatePresence>
          <Phone tab={next} size="w-[210px]" dimmed testid="showcase-phone-next" onClick={() => setActive((active + 1) % SHOWCASE_TABS.length)} />
        </div>

        <div data-testid="showcase-carousel" className="mt-12 md:hidden flex gap-5 overflow-x-auto snap-x snap-mandatory no-scrollbar pb-4 -mx-5 px-5">
          {SHOWCASE_TABS.map((t, i) => (
            <div key={t.id} className="snap-center shrink-0">
              <Phone tab={t} size="w-[220px]" dimmed={i !== active} testid={`showcase-mobile-phone-${t.id}`} onClick={() => setActive(i)} />
            </div>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.p
            key={tab.id}
            data-testid="showcase-caption"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="mt-10 text-center text-sm md:text-base text-slate-400 max-w-lg mx-auto"
          >
            {tab.caption}
          </motion.p>
        </AnimatePresence>
      </div>
    </section>
  );
};
